import * as React from 'react'

import { Download, Printer, Share2 } from 'lucide-react'

import { QRCode } from '~/client/src/shared/components/LazyQRCode'
import { ReactToPrint } from '~/client/src/shared/components/LazyReactToPrint'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '~/client/src/shared/components/shadcn-ui/dialog'
import { Input } from '~/client/src/shared/components/shadcn-ui/input'
import WorkflowTypeIcon from '~/client/src/shared/components/WorkflowTypeIcon/WorkflowTypeIcon'
import commonRoutes, {
  getTransitionPath,
} from '~/client/src/shared/constants/commonRoutes'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import WorkflowType from '~/client/src/shared/models/WorkflowType'

const QR_CODE_SIZE = 216
const QR_CODE_TO_PRINT_SIZE = 700
const QR_CODE_FG_COLOR = '#000000'

interface IProps {
  formType: WorkflowType
  projectCode: string
}

function getWorkflowQRCodeUrl(projectCode: string, formTypeId: string): string {
  const path = getTransitionPath(commonRoutes.FORMS, projectCode)
  return `${location.origin}${path}?formType=${formTypeId}`
}

export default function WorkflowQRCodesTab({ formType, projectCode }: IProps) {
  const printRef = React.useRef<HTMLDivElement>(null)
  const iconRef = React.useRef<HTMLDivElement>(null)
  const [isShareOpen, setIsShareOpen] = React.useState(false)

  const canvasId = `workflow-qr-${formType.type}`
  const url = getWorkflowQRCodeUrl(projectCode, formType.type)

  const handleDownload = React.useCallback(() => {
    const qrCanvas = document.getElementById(canvasId) as HTMLCanvasElement
    if (!qrCanvas) return

    const svgEl = iconRef.current?.querySelector('svg')
    if (!svgEl) {
      // Fallback: download QR only if icon not found
      const link = document.createElement('a')
      link.download = `${formType.name}-qr-code.png`
      link.href = qrCanvas.toDataURL('image/png')
      link.click()
      return
    }

    // Clone the SVG and replace currentColor with black
    const clone = svgEl.cloneNode(true) as SVGSVGElement
    clone.querySelectorAll('[fill="currentColor"]').forEach(el => {
      el.setAttribute('fill', '#000000')
    })
    clone.querySelectorAll('[stroke="currentColor"]').forEach(el => {
      el.setAttribute('stroke', '#000000')
    })

    const svgData = new XMLSerializer().serializeToString(clone)
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' })
    const svgUrl = URL.createObjectURL(svgBlob)

    const img = new Image()
    img.onload = () => {
      const size = qrCanvas.width
      const composite = document.createElement('canvas')
      composite.width = size
      composite.height = size
      const ctx = composite.getContext('2d')

      // Draw QR code
      ctx.drawImage(qrCanvas, 0, 0)

      // Draw white rounded background for icon
      const bgSize = Math.round(size * (60 / QR_CODE_SIZE))
      const bgX = (size - bgSize) / 2
      const bgY = (size - bgSize) / 2
      const radius = Math.round(size * (4 / QR_CODE_SIZE))
      ctx.fillStyle = '#ffffff'
      ctx.beginPath()
      ctx.roundRect(bgX, bgY, bgSize, bgSize, radius)
      ctx.fill()

      // Draw icon centered
      const iconSize = Math.round(size * (40 / QR_CODE_SIZE))
      const iconX = (size - iconSize) / 2
      const iconY = (size - iconSize) / 2
      ctx.drawImage(img, iconX, iconY, iconSize, iconSize)

      // Download composite
      const link = document.createElement('a')
      link.download = `${formType.name}-qr-code.png`
      link.href = composite.toDataURL('image/png')
      link.click()

      URL.revokeObjectURL(svgUrl)
    }
    img.src = svgUrl
  }, [canvasId, formType.name])

  const handleCopyLink = React.useCallback(() => {
    window.navigator.clipboard.writeText(url)
  }, [url])

  return (
    <div className="tw:flex tw:flex-col tw:gap-3 tw:w-[782px]">
      <span className="tw:text-title-md tw:text-foreground">
        {Localization.translator.xQRCode(formType.name)}
      </span>

      <div className="tw:border tw:border-border tw:rounded-lg tw:bg-background tw:p-4">
        <div className="tw:flex tw:flex-col tw:items-center tw:gap-1">
          <div className="tw:relative tw:inline-flex">
            <QRCode
              id={canvasId}
              value={url}
              size={QR_CODE_SIZE}
              fgColor={QR_CODE_FG_COLOR}
              quietZone={0}
            />
            <div
              ref={iconRef}
              className="tw:absolute tw:top-1/2 tw:left-1/2 tw:-translate-x-1/2 tw:-translate-y-1/2 tw:flex tw:items-center tw:justify-center tw:size-[60px] tw:bg-background tw:rounded tw:text-foreground"
            >
              <WorkflowTypeIcon
                iconName={formType.iconName}
                size="medium"
                className="tw:!max-w-10 tw:!max-h-10 tw:[&_svg]:!w-10 tw:[&_svg]:!h-10"
              />
            </div>
          </div>

          <div className="tw:flex tw:flex-col tw:items-center tw:gap-5 tw:w-full">
            <span className="tw:text-title-lg tw:text-foreground tw:text-center">
              {formType.name}
            </span>
            <div className="tw:flex tw:gap-2">
              <ReactToPrint
                trigger={() => (
                  <Button variant="outline" size="lg">
                    <Printer data-icon="inline-start" />
                    {Localization.translator.print}
                  </Button>
                )}
                content={() => printRef.current}
              />
              <Button
                variant="outline"
                size="lg"
                onClick={() => setIsShareOpen(true)}
              >
                <Share2 data-icon="inline-start" />
                {Localization.translator.share}
              </Button>
              <Button variant="default" size="lg" onClick={handleDownload}>
                <Download data-icon="inline-start" />
                {Localization.translator.download}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Hidden print target */}
      <div className="tw:hidden">
        <div
          ref={printRef}
          className="tw:flex tw:flex-col tw:items-center tw:p-8"
        >
          <div className="tw:relative tw:inline-flex">
            <QRCode
              value={url}
              size={QR_CODE_TO_PRINT_SIZE}
              fgColor={QR_CODE_FG_COLOR}
              quietZone={0}
            />
            <div className="tw:absolute tw:top-1/2 tw:left-1/2 tw:-translate-x-1/2 tw:-translate-y-1/2 tw:flex tw:items-center tw:justify-center tw:size-[160px] tw:bg-white tw:rounded tw:text-black">
              <WorkflowTypeIcon
                iconName={formType.iconName}
                size="medium"
                className="tw:!max-w-20 tw:!max-h-20 tw:[&_svg]:!w-20 tw:[&_svg]:!h-20"
              />
            </div>
          </div>
          <p className="tw:text-title-lg tw:text-black tw:text-center tw:mt-4">
            {formType.name}
          </p>
        </div>
      </div>

      {/* Share dialog */}
      <Dialog
        open={isShareOpen}
        onOpenChange={open => {
          if (!open) setIsShareOpen(false)
        }}
      >
        <DialogContent className="tw:w-[520px]">
          <DialogHeader>
            <DialogTitle>
              {Localization.translator.share} {formType.name}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <div className="tw:flex tw:flex-col tw:gap-2">
              <span className="tw:text-body-sm tw:text-muted-foreground">
                {Localization.translator.shareWithLink}
              </span>
              <div className="tw:flex tw:gap-2 tw:items-center">
                <Input value={url} readOnly className="tw:flex-1" />
                <Button variant="default" size="lg" onClick={handleCopyLink}>
                  {Localization.translator.copyLink}
                </Button>
              </div>
            </div>
          </DialogBody>
        </DialogContent>
      </Dialog>
    </div>
  )
}
